<template>
  <div class="profile-main-wrapper">
    <!-- Левая колонка (Аватар + ID) -->
    <div class="profile-avatar-section">
      <v-card class="profile-card pa-5" elevation="2">
        <v-avatar size="120">
          <v-img src="@/assets/profile-avatar.svg" />
        </v-avatar>
        <v-btn class="edit-icon" icon>
          <v-icon color="white">mdi-pencil</v-icon>
        </v-btn>
        <h3 class="mt-3">{{ form.name }}</h3>
        <p class="grey--text">id: {{ form.id }}</p>
      </v-card>
    </div>

    <!-- Правая колонка (Форма) -->
    <div class="profile-info-section">
      <v-card class="profile-card-info pa-5">
        <h2>{{ $t(`profile.accountDetails`) }}</h2>
        <v-row class="profile-forms-row" dense>
          <v-col
            v-for="(field, index) in formFields"
            :key="index"
            cols="6"
            class="profile-form-field"
          >
            <!-- Поле ФИО (только чтение) -->
            <v-text-field
              v-if="field.model === 'name'"
              :label="$t(`profile.${[field.model]}`)"
              outlined
              readonly
              disabled
              v-model="form[field.model]"
            />
            
            <!-- Поле телефона -->
            <v-text-field
              v-else-if="field.model === 'phone'"
              :label="$t(`profile.${[field.model]}`)"
              v-mask="'+7(###) ###-##-##'"
              auto-grow
              outlined
              rows="1"
              autocomplete="off"
              autocorrect="off"
              spellcheck="false"
              :disabled="field.disabled"
              v-model="form[field.model]"
              :error="$v.form[field.model].$error"
              :error-messages="getValidationError(field.model)"
              @blur="$v.form[field.model].$touch()"
            />
            
            <!-- Дополнительный телефон (необязательный) -->
            <v-text-field
              v-else-if="field.model === 'additionalPhone'"
              :label="$t(`profile.${[field.model]}`) + ' (необязательно)'"
              v-mask="'+7(###) ###-##-##'"
              auto-grow
              outlined
              rows="1"
              autocomplete="off"
              autocorrect="off"
              spellcheck="false"
              :disabled="field.disabled"
              v-model="form[field.model]"
              :error="$v.form[field.model].$error"
              :error-messages="getValidationError(field.model)"
              @blur="$v.form[field.model].$touch()"
              :hint="form[field.model] ? '' : 'Это поле можно оставить пустым'"
              persistent-hint
            />
            
            <!-- Поле даты выдачи -->
            <v-menu
              v-else-if="field.model === 'issueDate'"
              v-model="dateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="form.issueDate"
                  :label="$t(`profile.${field.model}`)"
                  readonly
                  outlined
                  v-bind="attrs"
                  v-on="on"
                  :error="$v.form.issueDate.$error"
                  :error-messages="getValidationError('issueDate')"
                  @blur="$v.form.issueDate.$touch()"
                />
              </template>
              <v-date-picker
                v-model="form.issueDate"
                no-title
                scrollable
                :max="maxDate"
                :locale="$i18n.locale"
                @input="dateMenu = false"
              />
            </v-menu>
            
            <!-- Поле номера документа -->
            <v-text-field
              v-else-if="field.model === 'docNumber'"
              :label="$t(`profile.${[field.model]}`)"
              outlined
              :disabled="field.disabled"
              v-model="form[field.model]"
              :counter="9"
              maxlength="9"
              :error="$v.form[field.model].$error"
              :error-messages="getValidationError(field.model)"
              @input="limitDocNumberLength"
            />
            
            <!-- Поле email -->
            <v-text-field
              v-else-if="field.model === 'email'"
              :label="$t(`profile.${[field.model]}`)"
              auto-grow
              outlined
              rows="1"
              :disabled="field.disabled"
              row-height="15"
              v-model="form[field.model]"
              :error="$v.form[field.model].$error"
              :error-messages="getValidationError(field.model)"
              @blur="$v.form[field.model].$touch(); validateEmail()"
            />
            
            <!-- Select поля -->
            <v-select
              v-else-if="field.model === 'docType' || field.model === 'issuedBy'"
              v-model="form[field.model]"
              :items="getSelectItems(field.model)"
              item-text="name"
              item-value="name"
              :label="$t(`profile.${field.model}`)"
              outlined
              :disabled="field.disabled"
              :error="$v.form[field.model].$error"
              :error-messages="getValidationError(field.model)"
              @blur="$v.form[field.model].$touch()"
            />
            
            <!-- Остальные поля -->
            <v-text-field
              v-else
              :label="$t(`profile.${[field.model]}`)"
              auto-grow
              outlined
              rows="1"
              :disabled="field.disabled"
              row-height="15"
              v-model="form[field.model]"
              :error="$v.form[field.model].$error"
              :error-messages="getValidationError(field.model)"
              @blur="$v.form[field.model].$touch()"
            />
          </v-col>
        </v-row>

        <!-- Кнопки -->
        <div class="profile-actions">
          <v-btn
            color="white"
            outlined
            class="profile-save-btn"
            @click="submitForm"
            :disabled="isSubmitLoading"
          >{{ $t(`profile.saveButton`) }}</v-btn
          >
        </div>
      </v-card>
    </div>
    <ProgressCircular
      :isSubmitLoading="isSubmitLoading"
      v-if="isLoading || isSubmitLoading"
    />
  </div>
</template>

<script>
import { validationMixin } from "vuelidate";
import { email, helpers, numeric, required } from "vuelidate/lib/validators";
import { mapActions, mapGetters } from "vuex";
import { showToast } from "@/utils/toast";
import ProgressCircular from "@/components/custom/ProgressCircular.vue";
import { saveProfile } from "@/api/profile";

const phoneMask = helpers.regex(
  "phoneMask",
  /^\+7\(\d{3}\)\s\d{3}-\d{2}-\d{2}$/
);

// Валидатор для номера документа (только цифры, максимум 9 символов)
const docNumberValidator = helpers.regex(
  "docNumberValidator",
  /^\d{1,9}$/
);

// Валидатор для даты (не может быть в будущем)
const notFutureDate = (value) => {
  if (!value) return true;
  const selectedDate = new Date(value);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return selectedDate <= today;
};

export default {
  name: "ProfileForm",
  components: { ProgressCircular },
  mixins: [validationMixin],

  data() {
    return {
      isLoading: false,
      isSubmitLoading: false,
      dateMenu: false,
      maxDate: new Date().toISOString().substr(0, 10), // Сегодняшняя дата как максимум
      form: {
        name: "",
        docType: "",
        email: "",
        docNumber: "",
        workplace: "",
        issuedBy: "",
        phone: "",
        issueDate: "",
        additionalPhone: "",
      },
      formFields: [
        { model: "name", disabled: true }, // ФИО нельзя менять
        { model: "docType", disabled: false },
        { model: "email", disabled: false },
        { model: "docNumber", disabled: false },
        { model: "workplace", disabled: false },
        { model: "issuedBy", disabled: false },
        { model: "phone", disabled: false },
        { model: "issueDate", disabled: false },
        { model: "additionalPhone", disabled: false },
      ],
    };
  },
  validations: {
    form: {
      name: { required },
      docType: { required },
      email: { required, email },
      docNumber: { required, docNumberValidator },
      workplace: { required },
      issuedBy: { required },
      phone: { required, phoneMask },
      issueDate: { required, notFutureDate },
      additionalPhone: { phoneMask }, // Необязательное поле
    },
  },
  created() {
    this.initializePageData();
  },
  mounted() {
    if (this.user) {
      this.form = { ...this.user };
    }
  },
  methods: {
    ...mapActions({
      fetchProfileData: "profileData/loadProfileData",
      fetchIssuedByData: "issuedBy/loadIssuedByData",
      fetchDocumentTypeData: "documentType/loadDocumentTypeData",
    }),
    
    formatPhoneNumber(number) {
      if (!number) return "";
      let clean = number.replace(/\D/g, "");

      if (clean.startsWith("8")) {
        clean = "7" + clean.slice(1);
      } else if (clean.startsWith("07")) {
        clean = "7" + clean.slice(1);
      } else if (clean.length === 10) {
        clean = "7" + clean;
      }

      if (clean.length !== 11 || !clean.startsWith("7")) return number;
      return clean.replace(
        /^(\d)(\d{3})(\d{3})(\d{2})(\d{2})$/,
        "+$1($2) $3-$4-$5"
      );
    },
    
    getSelectItems(model) {
      if (model === "docType") return this.docType;
      if (model === "issuedBy") return this.issuedBy;
      return [];
    },

    normalizePhone(phone) {
      if (!phone) return '';
      
      let cleaned = phone.replace(/[^\d+]/g, '');
      
      if (cleaned.startsWith('8')) {
        cleaned = '+7' + cleaned.substring(1);
      } else if (cleaned.startsWith('7') && !cleaned.startsWith('+7')) {
        cleaned = '+' + cleaned;
      } else if (!cleaned.startsWith('+') && cleaned.length >= 10) {
        cleaned = '+' + cleaned;
      }
      
      const phoneRegex = /^\+?[0-9]{10,15}$/;
      const digitsOnly = cleaned.replace('+', '');
      
      if (phoneRegex.test(cleaned) && digitsOnly.length >= 10 && digitsOnly.length <= 15) {
        return cleaned;
      }
      
      return cleaned;
    },

    // Ограничение длины номера документа
    limitDocNumberLength() {
      if (this.form.docNumber && this.form.docNumber.length > 9) {
        this.form.docNumber = this.form.docNumber.slice(0, 9);
      }
      // Удаляем все нецифровые символы
      this.form.docNumber = this.form.docNumber.replace(/\D/g, '');
    },

    // Дополнительная проверка email
    validateEmail() {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (this.form.email && !emailRegex.test(this.form.email)) {
        this.$v.form.email.$touch();
      }
    },

    async initializePageData() {
      if (this.isLoading) return;

      this.isLoading = true;
      this.isSubmitLoading = false;
      try {
        await Promise.all([
          this.fetchProfileData(),
          this.fetchIssuedByData(),
          this.fetchDocumentTypeData(),
        ]);
      } catch (error) {
        showToast(this, {
          text: `${this.$t(`toastText.errorData`)}: ${error}`,
          status: "error",
          duration: 3000,
        });
      } finally {
        this.isLoading = false;
      }
    },

    getValidationError(field) {
      const input = this.$v.form[field];
      if (!input.$dirty) return [];
      
      const messages = [];

      if (field === 'additionalPhone' && !input.$model) {
        return []; // Не показываем ошибку для пустого дополнительного телефона
      }

      if (input.required === false) messages.push(this.$t("validation.required"));
      if (input.email === false) messages.push(this.$t("validation.email"));
      if (input.numeric === false) messages.push(this.$t("validation.onlyNumber"));
      if (input.docNumberValidator === false) messages.push(this.$t("validation.maximum9"));
      if (input.phoneMask === false) messages.push(this.$t("validation.phone"));
      if (input.notFutureDate === false) messages.push(this.$t("validation.futureDate"));

      return messages;
    },

    async submitForm() {
      this.$v.form.$touch();
      
      // Проверяем все обязательные поля кроме additionalPhone
      const requiredFields = ['name', 'docType', 'email', 'docNumber', 'workplace', 'issuedBy', 'phone', 'issueDate'];
      const hasEmptyRequiredFields = requiredFields.some(field => !this.form[field] || this.form[field].toString().trim() === '');
      
      if (this.$v.form.$invalid || hasEmptyRequiredFields) {
        showToast(this, {
          text: this.$t(`toastText.fields`),
          status: "warning",
          duration: 3000,
        });
        return;
      }

      try {
        this.isSubmitLoading = true;
        const docTypeItem = this.docType.find(
          (item) => item.name === this.form.docType
        );
        const issuedByItem = this.issuedBy.find(
          (item) => item.name === this.form.issuedBy
        );

        const payload = {
          ...this.form,
          docType: docTypeItem?.id || null,
          issuedBy: issuedByItem?.id || null,
          phone: this.normalizePhone(this.form.phone),
          additionalPhone: this.form.additionalPhone ? this.normalizePhone(this.form.additionalPhone) : null,
        };
        
        console.log("Payload for submission:", payload);
        
        await saveProfile(payload);
        showToast(this, {
          text: this.$t(`toastText.successfullySaved`),
          status: "success",
          duration: 3000,
        });
      } catch (error) {
        showToast(this, {
          text: `${this.$t(`toastText.errorData`)}: ${error}`,
          status: "error",
          duration: 3000,
        });
      } finally {
        this.isSubmitLoading = false;
      }
    }
  },
  computed: {
    ...mapGetters("profileData", ["user"]),
    ...mapGetters("issuedBy", ["issuedBy"]),
    ...mapGetters("documentType", ["docType"]),
  },
  watch: {
    user: {
      handler(val) {
        if (val) {
          this.form = {
            ...val,
            phone: this.formatPhoneNumber(val.phone),
            additionalPhone: val.additionalPhone ? this.formatPhoneNumber(val.additionalPhone) : "",
          };
          this.isLoading = false;
        }
      },
      immediate: true,
    },
  },
};
</script>

<style scoped>
/* Стили остаются без изменений */
.profile-main-wrapper {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 20px;
}
.profile-card {
  text-align: center;
  width: 270px;
  position: relative;
  height: fit-content;
  padding: 0;
}
.profile-avatar-section {
  flex: 1;
  max-width: 270px;
}
.profile-info-section {
  flex: 2;
  min-width: 300px;
  max-width: 950px;
}
.profile-card-info {
  width: 950px;
  padding: 40px !important;
}
.edit-icon {
  position: absolute;
  top: 105px;
  right: 70px;
  background: #5e5af9;
}

.v-btn.profile-cancel-btn {
  background-color: #7e7e7e;
  margin-right: 8px;
  text-transform: none !important;
  border-radius: 8px;
}
.v-btn.profile-save-btn {
  background-color: #5e5af9 !important;
  color: #ffffff;
  border-radius: 8px;
  text-transform: none !important;
}
.profile-actions {
  display: flex;
  justify-content: end;
}

@media (max-width: 1024px) {
  .profile-main-wrapper {
    flex-direction: column;
    align-items: center;
  }
  .profile-info-section {
    width: 100%;
  }
}

@media (max-width: 768px) {
  .profile-card-info {
    padding: 20px;
  }
  .profile-info-section {
    padding: 0 25px;
  }
  .profile-actions {
    justify-content: center;
  }
  .profile-forms-row {
    flex-direction: column;
  }
  .profile-form-field {
    max-width: 100%;
  }
  .v-btn.profile-save-btn {
    padding: 25px 20px;
    margin: 0px auto;
    margin-top: 20px;
  }
}

@media (max-width: 480px) {
  .profile-actions {
    flex-direction: column;
    gap: 10px;
  }
  .v-btn.profile-cancel-btn {
    margin-right: unset;
  }
}
</style>